import { Suspense, useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useLazyQuery, useQuery } from '@apollo/client';

import { meQuery, UserResponse, User } from '@libs/graphql';
import { JurisdictionsEnum } from '@libs/backend-common';

import {
  DashboardNav,
  DashboardHeader,
  DashboardMobileNav,
  JurisdictionRenderer,
} from 'src/components';
import { DashboardBlurImg } from 'src/assets';
import { setOnUnauthorized } from 'src/setup';
import { getPlatformUnderConstructionStatusQuery } from 'src/gql';
import {
  useApiResponse,
  useAuth,
  useRenderObjectByTheme,
  POP_UP_MODAL_PARENT_ID,
  useAppDisclaimer,
  useRefreshTokenActionContext,
} from 'src/hooks';
import { GTMEventNames } from 'src/types';
import { pushGTMEvent } from 'src/helpers';

import { RefreshTokenActionProvider } from 'src/contexts';
import { LayoutPopUps } from './components/LayoutPopUps';

import {
  BlurImage,
  BlurImageRight,
  ContentContainer,
  ContentWrapper,
  DashboardContainer,
} from './styles';

const Dashboard = () => {
  const location = useLocation();
  const refreshTokenContext = useRefreshTokenActionContext();

  const navigate = useNavigate();
  const userRef = useRef<User | undefined>();

  const { data, loading } = useQuery(meQuery, { fetchPolicy: 'network-only' });

  useApiResponse({ data, loading }, (data: UserResponse) => {
    if (userRef.current) return;
    userRef.current = data.me;
    const user_first_name =
      data.me.officialFirstName || data.me.firstName || '';
    const user_last_name = data.me.officialLastName || data.me.lastName || '';
    pushGTMEvent(GTMEventNames.init, {
      user_id: data.me.id,
      user_first_name,
      user_last_name,
      user_email: data.me.email || '',
      user_phone: data.me.phone || '',
      ref_code: data.me.referrerCode || '',
      user_address: data.me.address || '',
      user_postcode: data.me.zip || '',
      user_city: data.me.city || '',
      user_country: data.me.country || '',
    });
  });

  const DashboardBlurImage = useRenderObjectByTheme(
    undefined,
    DashboardBlurImg
  );

  const onLogout = useCallback(() => {
    navigate('/login', {
      replace: true,
    });
  }, [navigate]);

  const { logout } = useAuth(onLogout);

  useEffect(() => {
    setOnUnauthorized(logout);

    return () => setOnUnauthorized(() => ({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [fetchStatus, { data: statusData }] = useLazyQuery(
    getPlatformUnderConstructionStatusQuery,
    { fetchPolicy: 'network-only' }
  );
  const { response: statusRes } = useApiResponse({
    data: statusData,
  });

  useEffect(() => {
    fetchStatus();
  }, [location.pathname, fetchStatus]);

  const isUnderConstruction = statusRes?.getPlatformUnderConstructionStatus;

  const { isAppDisclaimerVisible } = useAppDisclaimer();

  return (
    <RefreshTokenActionProvider value={refreshTokenContext}>
      <div id={POP_UP_MODAL_PARENT_ID} />
      <LayoutPopUps
        isUnderConstruction={isUnderConstruction}
        isAppDisclaimerVisible={isAppDisclaimerVisible}
      />
      <DashboardContainer data-cy="dashboard">
        <DashboardNav
          testId="dashboardNav"
          isUnderConstruction={isUnderConstruction}
        />
        <ContentContainer
          isUnderConstruction={isUnderConstruction}
          isAppDisclaimerVisible={isAppDisclaimerVisible}
        >
          <JurisdictionRenderer
            acceptedJurisdictions={[JurisdictionsEnum.UA, JurisdictionsEnum.UA]}
          >
            <DashboardHeader
              testId="dashboardHeader"
              isUnderConstruction={isUnderConstruction}
            />
          </JurisdictionRenderer>
          <ContentWrapper
            isDashboard={location.pathname === '/'}
            data-cy="dashboardContentWrapper"
          >
            <JurisdictionRenderer
              acceptedJurisdictions={[JurisdictionsEnum.UA]}
            >
              <BlurImage src={DashboardBlurImage} />
              <BlurImageRight src={DashboardBlurImage} />
            </JurisdictionRenderer>

            {/* NOTE: Dashboard's contents render here*/}
            <Suspense fallback={<></>}>
              <Outlet />
            </Suspense>
          </ContentWrapper>
          {!window.isFromMobileApp && (
            <DashboardMobileNav testId="dashboardMobleNav" />
          )}
        </ContentContainer>
      </DashboardContainer>
    </RefreshTokenActionProvider>
  );
};

export default Dashboard;
